<template>
  <div>

    <!-- Search -->
    <div ref="index" class="row">
      <div class="col-md-4">
        <input
          class="input form-control"
          v-model="query.generic"
          @keypress.enter="find()"
          placeholder="Pesquisar por CPF ou Nome"
        >
        <search-icon @click="find()" class="icon icon-within"/>
      </div>

      <button
        class="col-md-2 ml-2 btn-sm btn-outline"
        style="min-height:40px;"
        :class="{ 'btn-outline-active': showFilter }"
        @click="showFilter = !showFilter"
      >
        Filtros
        <chevron-down-icon class="fa-angle-right" :class="{'rotate-90': showFilter }"/>
      </button>

      <button v-if="changeQuery" @click="find()" class="btn btn-link">
        Aplicar filtros ({{ numberOfBadges }})
      </button>
    </div>

    <!-- Badges -->
    <div class="row">
      <div class="col-md-12">
        <BadgeFilters
          :query="query" @clearBadge="find()"
          @clearAllBadges="find()"
          :amount-changed="updateNumberOfBadges"
          :filters="badgesFilters"
        />
      </div>
    </div>

    <!-- Filters -->
    <template v-if="showFilter">
      <div class="row mt-4">
        <div class="col-md-4">
          <label for="driver-category">Categoria</label>
          <Select2 :options="filterCategory" v-model="query.category" id="driver-category" />
        </div>

        <div class="col-md-4">
          <label for="driver-cnh-file-attached">CNH</label>
          <Select2 :options="filterCnhFileAttached" v-model="query.cnh_file_attached" id="driver-cnh-file-attached" />
        </div>

      </div>

      <div class="row mt-4">
        <div class="col-md-4">
          <label for="type-period">Tipo do período</label>
          <select v-model="query.periodType" id="type-period" class="browser-default custom-select">
            <option value="updated_at">Data de atualização</option>
            <option value="created_at">Data de criação</option>
          </select>
        </div>

        <div class="col-md-4">
          <label for="start-date">Data inicial</label>
          <input v-model="query.startDate" type="date" id="start-date" class="form-control">
        </div>

        <div class="col-md-4">
          <label for="end-date">Data final</label>
          <input v-model="query.endDate" type="date" id="end-date" class="form-control">
        </div>
      </div>

    </template>
    <hr>

    <!-- Buttons -->

    <Dropdown
        v-if="$withPolicy('application', 'drivers/insert_or_update')"
        title-dropdown="Cadastro de motoristas"
        class="btn btn-primary mr-1 "
    >
      <ul class="mb-1 mt-1">
        <li
            class="dropdown-item"
            @click="$emit('createDriver')"
        >
          Cadastrar novo motorista
        </li>
        <li
            class="dropdown-item"
            @click="showModal = true"
        >
          Cadastrar motoristas em lote
        </li>
      </ul>
    </Dropdown>

    <button
        class="btn btn-primary mr-1"
        @click="exportXLSX()"
        :disabled="disabledButton"
    >
      Exportar relatório
    </button>

    <!-- Info -->
    <div class="row mt-4">
      <div class="col-md-12">
        <small>
          {{ infoTotalModels }}
        </small>

        <button
            v-if="meta.totalCount > 0"
            class="btn btn-outline ml-3 mb-1"
            :class="{ 'btn-outline-active': checkAll }"
            @click="changeCheck({checkAll: !checkAll, sources: drivers})"
        >
          {{ !checkAll ? 'Selecionar todos' : 'Desmarcar todos' }} ({{ meta.totalCount || 'Calculando' }})
        </button>

        <div class="float-right" style="display: inline-flex;align-items: center;">
          <small class="mr-2" style="white-space: nowrap">Ordenar por:</small>
          <select v-model="optionOrder" @change="updateOrder(); find()" class="browser-default custom-select">
            <option :value="'name_asc'">Nome (A-Z)</option>
            <option :value="'name_desc'">Nome (Z-A)</option>
            <option :value="'created_at'">Data de criação</option>
            <option :value="'updated_at'">Data de atualização</option>
          </select>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12">
        <Table
          :drivers="drivers"
          :checkAllRow="checkAllRow"
          @showDriver="$emit('showDriver', $event)"
          @sendDeleteDriver="$emit('sendDeleteDriver', $event)"
          @changeCheckAllRow="changeCheckAllRow"
          @changeCheckRow="changeCheckRow"
        />

        <div class="row">
          <div class="col-md-12">
            <Pagination
              v-model="meta.currentPage"
              :total-count="meta.totalCount"
              :total-pages="meta.totalPages"
              @pagination="$emit('pagination', $event)"
            />
          </div>
        </div>
      </div>
    </div>

    <Modal v-model="showModal">
      <span slot="header">Upload de arquivo CSV</span>
      <p class="my-2">Baixe o arquivo modelo, preencha com as informações dos motoristas e faça o upload aqui.</p>
      <Upload :isMultiple="false" accept=".csv, .CSV" @changeFiles="changeFiles">
        <h5>Clique ou arraste o arquivo CSV preenchido para carregar os motoristas em lote</h5>
      </Upload>
      <template slot="footer">
        <button
            class="btn btn-primary mt-1 mr-1"
            @click="downloadTemplate"
        >
          Baixar arquivo modelo
        </button>
      </template>
    </Modal>

    <BatchDriversModal
      v-model="showReviewModal"
      :rows="batchRows"
      :duplicates-removed="duplicatesRemoved"
      @scheduled="find()"
    />
  </div>
</template>

<script>
  import BadgeFilters from '@/components/shares/BadgeFilters'
  import GetAccount from '@/mixins/GetAccount.vue'
  import getUser from '@/mixins/GetUser.vue'

  import CheckedModels from '@/mixins/CheckedModels'

  import Modal from '@/components/shares/specialTag/Modal'
  import Pagination from '@/components/shares/specialTag/Pagination'
  import Select2 from '@/components/shares/Select2'

  import Table from '@/components/drivers/container/Table'
  import Upload from '@/components/shares/specialTag/Upload'
  import BatchDriversModal from '@/components/drivers/container/BatchDriversModal'

  import GlobalLoading from '@/mixins/GlobalLoading'
  import { ChevronDownIcon, SearchIcon } from 'vue-feather-icons'
  import { Filter } from '@/models/filter'
  import Dropdown from '@/components/shares/specialTag/Dropdown.vue'

  export default {
    components: {
      BadgeFilters, BatchDriversModal, ChevronDownIcon, Dropdown,
      Modal,
      Pagination, SearchIcon, Select2, Table,
      Upload,
    },
    computed: {
      disabledButton() {
        return this.drivers.filter(v => v.checked).length === 0
      },
      badgesFilters() {
        return [
          this.filterCategory, this.filterCnhFileAttached,
          this.filterEndDate, this.filterStartDate
        ]
      },
    },
    created() {
      this.updateOrder()
      this.loadCategories()
    },
    data: () => ({
      showModal: false,
      showReviewModal: false,
      batchRows: [],
      duplicatesRemoved: 0,
      showFilter: false,
      optionOrder: 'updated_at',
      filterCategory: new Filter({
        key: 'category',
        label: 'Categoria',
        type: 'select',
        table: []
      }),
      filterCnhFileAttached: new Filter({
        key: 'cnh_file_attached',
        label: 'CNH',
        type: 'select',
        table: [
          { label: 'Pendente', value: 'false' },
          { label: 'Anexado', value: 'true' }
        ]
      }),
      filterEndDate: new Filter({
        key: 'endDate',
        label: 'Data final',
        type: 'date'
      }),
      filterStartDate: new Filter({
        key: 'startDate',
        label: 'Data inicial',
        type: 'date'
      }),
    }),
    mixins: [CheckedModels, GetAccount, GlobalLoading, getUser],
    name: 'Index',
    methods: {
      exportXLSX() {
        if (this.disableExport)
          return

        let params
        let amount

        if (this.checkAll) {
          params = this.buildParams()
          amount = this.meta.totalCount
        } else {
          const ids = this.drivers.filter(c => c.checked).map(c => c.id)
          params = { q: { id: ids } }
          amount = ids.length
        }

        const message = amount === 1 ?
          'Exportar os dados do motorista selecionado?' :
          `Exportar os dados de ${amount} motoristas?`

        if (!confirm(message))
          return

        this.openGlobalLoad()
        this.axios.get('drivers/report', {params})
          .then(response => {
            if (response.data)
              this.$parse.downloadByLink(response.data)
            else
              this.$notify.add(response.meta.notice)
          })
          .catch(e => this.$notify.addError(e))
          .finally(() => this.closeGlobalLoad())
      },
      changeFiles(files) {
        const file = files[0]
        const reader = new FileReader()

        reader.onload = (e) => {
          try {
            const buffer = e.target.result
            const utf8 = new TextDecoder('utf-8', { fatal: false }).decode(buffer)
            const text = utf8.includes('�')
              ? new TextDecoder('windows-1252').decode(buffer)
              : utf8
            const firstLine = text.split(/\r?\n/).find(l => l.trim().length) || ''
            const separator = [';', ',', '\t']
              .map(sep => ({ sep, count: firstLine.split(sep).length }))
              .sort((a, b) => b.count - a.count)[0].sep

            const matrix = text.split(/\r?\n/)
              .map(line => line.split(separator).map(c => c.replace(/["\uFEFF\u200B]/g, '').trim()))
              .filter(line => line.some(c => c.length > 0))

            if (!matrix.length)
              throw 'Arquivo vazio.'

            const headerMap = { nome: 'name', cpf: 'cpf', telefone: 'phone', categoria: 'category' }
            const header = matrix[0].map(h => h.replace(/[\uFEFF\u200B]/g, '').trim().toLowerCase())
            const fieldIndexes = {}
            for (const [pt, en] of Object.entries(headerMap)) {
              const idx = header.indexOf(pt)
              if (idx === -1)
                throw `Cabeçalho inválido: coluna "${pt}" não encontrada. Esperado: Nome;CPF;Telefone;Categoria.`
              fieldIndexes[en] = idx
            }

            const seen = new Set()
            const seenCpf = new Set()
            let duplicatesRemoved = 0
            const rows = []
            for (const line of matrix.slice(1)) {
              // 1ª tratativa: remove linhas idênticas (conteúdo inteiro)
              const key = line.join('|')
              if (seen.has(key)) {
                duplicatesRemoved++
                continue
              }
              seen.add(key)

              // 2ª tratativa: remove CPFs repetidos, mantendo a primeira ocorrência
              const cpfKey = (line[fieldIndexes.cpf] || '').replace(/\D/g, '')
              if (cpfKey && seenCpf.has(cpfKey)) {
                duplicatesRemoved++
                continue
              }
              if (cpfKey) seenCpf.add(cpfKey)

              rows.push({
                name: line[fieldIndexes.name] || '',
                cpf: line[fieldIndexes.cpf] || '',
                phone: line[fieldIndexes.phone] || '',
                category: line[fieldIndexes.category] || '',
              })
            }

            this.batchRows = rows
            this.duplicatesRemoved = duplicatesRemoved
            this.showModal = false
            this.showReviewModal = true
          } catch (err) {
            this.$notify.addError(err)
          }
        }

        reader.readAsArrayBuffer(file)
      },
      downloadTemplate() {
        const csv = 'Nome;CPF;Telefone;Categoria\n'
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        this.$parse.buildDownload('Template - Cadastro de motoristas.csv', blob)
      },
      find() {
        this.$emit('find')
      },
      loadCategories() {
        this.axios.get('drivers/fetch_column/category')
          .then(response => {
            this.filterCategory = new Filter({
              key: 'category',
              label: 'Categoria',
              type: 'select',
              table: response.data.filter(d => d).map(category => [category, category])
            })
          })
      },
      updateOrder() {
        this.order.length = 0
        switch (this.optionOrder) {
          case 'name_asc':
            this.order.push('name', 'asc')
            break
          case 'name_desc':
            this.order.push('name', 'desc')
            break
          case 'created_at':
            this.order.push('created_at', 'desc')
            break
          case 'updated_at':
          default:
            this.order.push('updated_at', 'desc')
            break
        }
      },
      changeCheckAllRow(value) {
        this.changeCheck({ checkAllRow: value, sources: this.drivers })
      },
      changeCheckRow(){
        this.changeCheck({ sources: this.drivers })
      },
    },
    props: {
      meta: { required: true, type: Object },
      query: { required: true, type: Object },
      order: { required: true, type: Array },
      drivers: { required: true, type: Array },
      changeQuery: { required: true, type: Boolean },
      numberOfBadges: { required: true, type: Number },
      updateNumberOfBadges: { required: true, type: Function },
      buildParams: { required: true, type: Function },
      infoTotalModels: { required: true, type: String },
    }
  }
</script>

<style scoped>
